import { AdminProviderRef, ProviderDeploymentConfig } from "@equipped/core/admin/providerDeployment";

/** The legacy/default id is intentionally stable: auth storage preserves its old keys. */
export const DEFAULT_ADMIN_PROVIDER_ID = "default";

export interface AdminDeploymentEnv {
  VITE_ADMIN_DEPLOYMENT?: string;
  VITE_ADMIN_FEATURES?: string;
  VITE_BACKEND_URL?: string;
  VITE_ADMIN_PROVIDERS?: string;
  VITE_ADMIN_HOSTNAME_MAP?: string;
  VITE_IOS_STORE_URL?: string;
  VITE_ANDROID_STORE_URL?: string;
  VITE_IOS_TESTFLIGHT_URL?: string;
}

export interface AdminStoreLinksConfig {
  iosStoreUrl: string;
  androidStoreUrl: string;
  iosTestFlightUrl: string;
}

export type AdminDeploymentKind = "owner" | "provider";

/** Shared interpretation of docs/358's existing deployment selector. */
export function resolveAdminDeploymentKind(env: AdminDeploymentEnv): AdminDeploymentKind {
  return env.VITE_ADMIN_DEPLOYMENT?.trim().toLowerCase() === "provider" ? "provider" : "owner";
}

function parseProviders(raw: string | undefined): AdminProviderRef[] | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const providers = parsed.filter(
      (value): value is AdminProviderRef =>
        !!value &&
        typeof value === "object" &&
        typeof (value as AdminProviderRef).id === "string" &&
        typeof (value as AdminProviderRef).label === "string" &&
        typeof (value as AdminProviderRef).backendUrl === "string",
    );
    return providers.length > 0 ? providers : null;
  } catch {
    return null;
  }
}

function parseHostnameMap(raw: string | undefined): Record<string, string> | undefined {
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return undefined;
    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, string] => typeof entry[1] === "string"),
    );
  } catch {
    return undefined;
  }
}

/**
 * Build-time hook for docs/345. Unset env keeps today's one `VITE_BACKEND_URL`
 * provider. `VITE_ADMIN_PROVIDERS`/`VITE_ADMIN_HOSTNAME_MAP` are public JSON
 * config for later multi-provider wiring; they never contain credentials.
 */
export function resolveProviderConfigFromEnv(env: AdminDeploymentEnv): ProviderDeploymentConfig {
  const deployment = resolveAdminDeploymentKind(env);
  const configuredProviders = parseProviders(env.VITE_ADMIN_PROVIDERS);
  const backendUrl = env.VITE_BACKEND_URL ?? "";
  const providers = configuredProviders ?? [
    {
      id: DEFAULT_ADMIN_PROVIDER_ID,
      label: deployment === "owner" ? "Be Equipped" : "Provider",
      backendUrl,
    },
  ];

  return {
    mode: providers.length > 1 ? "multi" : "single",
    providers,
    hostnameMap: parseHostnameMap(env.VITE_ADMIN_HOSTNAME_MAP),
    defaultProviderId: providers[0]?.id,
  };
}

export const ADMIN_PROVIDER_CONFIG = resolveProviderConfigFromEnv(import.meta.env);

/** Public install destinations used by the App Links page. Empty means not published yet. */
export function resolveStoreLinksFromEnv(env: AdminDeploymentEnv): AdminStoreLinksConfig {
  return {
    iosStoreUrl: env.VITE_IOS_STORE_URL?.trim() ?? "",
    androidStoreUrl: env.VITE_ANDROID_STORE_URL?.trim() ?? "",
    iosTestFlightUrl: env.VITE_IOS_TESTFLIGHT_URL?.trim() ?? "",
  };
}

export const ADMIN_STORE_LINKS_CONFIG = resolveStoreLinksFromEnv(import.meta.env);
